// MARK://dhk8/admin-shell/lib/clients/impersonate
// Manager override / "Ver como…" — flow para que el dueño suplante a un usuario
// del tenant (manager / cajero / mesero) sin necesidad de su PIN. Auditado en
// dhk8-platform-audit (hash-chain Ed25519 — ADR-0021).
//
// Backend:
//   POST /api/users/:id/impersonate              (dhk8-platform-auth)
//        → 200 { ok, data: { sessionToken, userId, fullName, role, tenantId } }
//   POST /api/audit/log                          (dhk8-platform-audit)
//        → 201 { ok, data: { id, ... } }
//
// Hoy POST /api/users/:id/impersonate está REGISTRADA en module.ts pero el
// handler aún no existe (ver services/dhk8-platform-auth/src/api/users.routes.ts).
// Mientras tanto, en modo preview/stub devolvemos un objeto sintético; en modo
// real degradamos a un no-op si el backend responde 404/501 y dejamos que el
// store haga el UI-only flag.
//
// El audit log SIEMPRE intentamos enviarlo (best-effort; si dhk8-platform-audit
// no responde, no rompemos el flujo — el banner de suplantación sigue siendo la
// pista visual obligatoria).
import { svcUrl, apiData, apiFetch, ApiError } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'

const AUTH_SVC = 'dhk8-platform-auth'
const AUDIT_SVC = 'dhk8-platform-audit'

export interface ImpersonationToken {
  /** Bearer JWT for the impersonated session (or null in dev/preview). */
  sessionToken: string | null
  userId: string
  fullName: string
  role: string
  tenantId: string
}

/** Issue an impersonation session for `userId`. Returns null if the backend
 *  endpoint isn't deployed (404 / 501); callers should still flip the UI flag. */
export async function startImpersonation(userId: string): Promise<ImpersonationToken | null> {
  if (PREVIEW_STUB) {
    return mock({ sessionToken: null, userId, fullName: '(stub)', role: 'manager', tenantId: 'tnt_demo' })
  }
  try {
    return await apiData<ImpersonationToken>(svcUrl(AUTH_SVC, `/api/users/${userId}/impersonate`), { method: 'POST' })
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 501)) return null
    throw e
  }
}

export type ImpersonationEvent = 'IMPERSONATION_START' | 'IMPERSONATION_END'

/** Log an impersonation event to dhk8-platform-audit. Best-effort: errors are
 *  swallowed so the UI flow never blocks on the audit pipeline. */
export async function logImpersonation(input: {
  event: ImpersonationEvent
  tenantId: string
  actorUserId: string
  targetUserId: string
  targetName?: string
}): Promise<void> {
  if (PREVIEW_STUB) return
  try {
    await apiFetch<unknown>(svcUrl(AUDIT_SVC, '/api/audit/log'), {
      method: 'POST',
      body: {
        tenantId: input.tenantId,
        eventName: input.event === 'IMPERSONATION_START'
          ? 'dhk8.platform.user.impersonated'
          : 'dhk8.platform.user.impersonation_ended',
        actorType: 'user',
        actorId: input.actorUserId,
        payload: { targetUserId: input.targetUserId, targetName: input.targetName ?? null },
        severity: 'high',
      },
    })
  } catch { /* swallow — audit is best-effort */ }
}
